/**
 * Related Projects Section
 *
 * Displays 2-4 related projects after the FAQ section to encourage exploration.
 * Shows tool overlap indicators ("Same kit" or "+2 tools needed").
 */

import { Link } from '@tanstack/react-router'
import {
  Badge,
  Box,
  Card,
  Group,
  Image,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { IconCheck, IconClock, IconCurrencyDollar, IconTool } from '@tabler/icons-react'
import { usePostHog } from '@/lib/posthog'
import { trackRelatedProjectClicked } from '@/features/diy/utils/analytics'

interface RelatedProject {
  _id: string
  slug: string
  title: string
  description: string
  category: string
  difficulty: string
  estimatedHours?: number
  estimatedCostMin?: number
  estimatedCostMax?: number
  heroImageUrl?: string
  toolOverlapCount: number
  additionalToolsNeeded: number
}

interface RelatedProjectsProps {
  projects: Array<RelatedProject>
  /** Current template slug (for analytics source) */
  sourceProjectSlug: string
}

const DIFFICULTY_COLORS: Record<string, string> = {
  beginner: 'green',
  intermediate: 'yellow',
  advanced: 'red',
}

function ToolOverlapBadge({
  toolOverlapCount,
  additionalToolsNeeded,
}: {
  toolOverlapCount: number
  additionalToolsNeeded: number
}) {
  if (additionalToolsNeeded === 0 && toolOverlapCount > 0) {
    return (
      <Badge
        size="xs"
        variant="light"
        color="green"
        leftSection={<IconCheck size={10} />}
      >
        Same kit
      </Badge>
    )
  }

  if (additionalToolsNeeded > 0) {
    return (
      <Badge
        size="xs"
        variant="outline"
        color="gray"
        leftSection={<IconTool size={10} />}
      >
        +{additionalToolsNeeded} tool{additionalToolsNeeded > 1 ? 's' : ''}
      </Badge>
    )
  }

  return null
}

function RelatedProjectCard({
  project,
  onNavigate,
}: {
  project: RelatedProject
  onNavigate: () => void
}) {
  return (
    <Link
      to="/diy/$slug"
      params={{ slug: project.slug }}
      style={{ textDecoration: 'none' }}
      onClick={onNavigate}
    >
      <Card
        withBorder
        padding="sm"
        radius="md"
        style={{ cursor: 'pointer', height: '100%' }}
      >
        <Stack gap="xs">
          {project.heroImageUrl && (
            <Image
              src={project.heroImageUrl}
              alt={project.title}
              height={100}
              radius="sm"
              fit="cover"
            />
          )}

          <Text fw={600} size="sm" lineClamp={2}>
            {project.title}
          </Text>

          <Group gap="xs" wrap="wrap">
            <Badge
              size="xs"
              variant="light"
              color={DIFFICULTY_COLORS[project.difficulty] ?? 'gray'}
            >
              {project.difficulty}
            </Badge>

            <ToolOverlapBadge
              toolOverlapCount={project.toolOverlapCount}
              additionalToolsNeeded={project.additionalToolsNeeded}
            />
          </Group>

          <Group gap="md">
            {project.estimatedHours && (
              <Group gap={4}>
                <IconClock size={12} color="var(--mantine-color-dimmed)" />
                <Text size="xs" c="dimmed">
                  {project.estimatedHours}h
                </Text>
              </Group>
            )}

            {(project.estimatedCostMin || project.estimatedCostMax) && (
              <Group gap={4}>
                <IconCurrencyDollar size={12} color="var(--mantine-color-dimmed)" />
                <Text size="xs" c="dimmed">
                  ${project.estimatedCostMin ?? 0}
                  {project.estimatedCostMax && project.estimatedCostMax !== project.estimatedCostMin
                    ? `-${project.estimatedCostMax}`
                    : ''}
                </Text>
              </Group>
            )}
          </Group>
        </Stack>
      </Card>
    </Link>
  )
}

export function RelatedProjects({ projects, sourceProjectSlug }: RelatedProjectsProps) {
  const posthog = usePostHog()

  if (projects.length === 0) {
    return null
  }

  return (
    <Box mt="xl">
      <Title order={3} size="h4" mb="md">
        Continue Your Journey
      </Title>

      <SimpleGrid cols={{ base: 2, sm: 2, md: 4 }} spacing="md">
        {projects.map((project, index) => (
          <RelatedProjectCard
            key={project._id}
            project={project}
            onNavigate={() => {
              trackRelatedProjectClicked(posthog, {
                sourceProjectSlug,
                targetProjectSlug: project.slug,
                position: index,
                toolOverlap:
                  project.additionalToolsNeeded === 0 && project.toolOverlapCount > 0
                    ? 'same_kit'
                    : 'additional_tools',
              })
            }}
          />
        ))}
      </SimpleGrid>
    </Box>
  )
}
